import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Building2,
  MapPin,
  Wrench,
  Coins,
  MonitorPlay,
  Cpu,
} from "lucide-react";
import Reveal from "../components/Reveal";
import {
  stats,
  marqueeServices,
  brands,
  whatWeDo,
  projects,
  heroImage,
} from "../mock/data";

const iconMap = { Building2, MapPin, Wrench, Coins, MonitorPlay, Cpu };

const Home = () => {
  const featured = projects.filter((p) => p.featured);

  return (
    <main>
      {/* HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: "100vh", display: "flex", alignItems: "center" }}>
        <div className="absolute inset-0">
          <img src={heroImage} alt="City billboards at night" className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(11,11,13,0.72) 0%, rgba(11,11,13,0.82) 45%, rgba(11,11,13,0.97) 100%)",
            }}
          />
        </div>
        <div className="glow-orb" style={{ width: 420, height: 420, background: "var(--oo-orange)", top: "12%", right: "-6%", opacity: 0.28 }} />

        <div className="oo-container relative" style={{ paddingTop: 120, paddingBottom: 80 }}>
          <Reveal>
            <span className="oo-eyebrow">Out Of Home Media & Adtech</span>
          </Reveal>
          <Reveal delay={80}>
            <h1
              className="font-display font-extrabold"
              style={{ fontSize: "clamp(40px, 7vw, 84px)", lineHeight: 1.02, marginTop: 22, maxWidth: 960, letterSpacing: "-0.02em" }}
            >
              Purpose Driven <span className="text-accent">Reach</span> For Your Brand
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p style={{ color: "var(--oo-muted)", fontSize: "clamp(16px,2vw,20px)", lineHeight: 1.7, maxWidth: 620, marginTop: 28 }}>
              OOHMA Multimedia is an integrated out-of-home media and infrastructure company. We combine design, fabrication, and media monetisation to create long-term advertising assets across cities.
            </p>
          </Reveal>
          <Reveal delay={240}>
            <div className="flex flex-wrap gap-4" style={{ marginTop: 40 }}>
              <Link to="/work" className="btn-primary">
                View Our Work <ArrowUpRight size={18} />
              </Link>
              <Link to="/contact" className="btn-ghost">
                Let's Talk
              </Link>
            </div>
          </Reveal>

          <Reveal delay={320}>
            <div className="grid grid-cols-3 gap-4 md:gap-10" style={{ marginTop: 72, maxWidth: 680 }}>
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display font-extrabold text-accent" style={{ fontSize: "clamp(30px,5vw,52px)", lineHeight: 1 }}>
                    {s.value}
                  </div>
                  <div style={{ color: "var(--oo-muted)", fontSize: 14, marginTop: 8 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARQUEE */}
      <section style={{ borderTop: "1px solid var(--oo-border)", borderBottom: "1px solid var(--oo-border)", padding: "26px 0", background: "var(--oo-bg-2)" }}>
        <div className="marquee">
          <div className="marquee-track">
            {[...marqueeServices, ...marqueeServices].map((s, i) => (
              <span key={i} className="font-display inline-flex items-center" style={{ fontSize: 20, fontWeight: 600, color: "var(--oo-faint)", padding: "0 28px" }}>
                {s}
                <span className="text-accent" style={{ marginLeft: 28 }}>•</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* TRUSTED BY */}
      <section className="oo-section">
        <div className="oo-container">
          <Reveal>
            <p className="text-center oo-eyebrow" style={{ color: "var(--oo-faint)" }}>Trusted By India's Leading Brands</p>
          </Reveal>
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-6" style={{ marginTop: 36 }}>
            {brands.map((b, i) => (
              <Reveal key={b} delay={i * 40}>
                <span className="font-display" style={{ fontSize: "clamp(16px,2.2vw,22px)", fontWeight: 600, color: "var(--oo-faint)", transition: "color 0.25s" }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = "var(--oo-text)")}
                  onMouseLeave={(e) => (e.currentTarget.style.color = "var(--oo-faint)")}
                >
                  {b}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT WE DO */}
      <section className="oo-section" style={{ background: "var(--oo-bg-2)" }}>
        <div className="oo-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(32px,5vw,56px)", letterSpacing: "-0.02em" }}>
                What We <span className="text-accent">Do</span>
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <Link to="/services" className="hover-underline font-display inline-flex items-center gap-2" style={{ color: "var(--oo-muted)" }}>
                Explore all services <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" style={{ marginTop: 52 }}>
            {whatWeDo.map((item, i) => {
              const Icon = iconMap[item.icon];
              return (
                <Reveal key={item.no} delay={(i % 3) * 80}>
                  <div className="oo-card h-full" style={{ padding: 32 }}>
                    <div className="flex items-center justify-between">
                      <div className="inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: 16, background: "var(--oo-orange-soft)" }}>
                        <Icon size={26} className="text-accent" />
                      </div>
                      <span className="font-display" style={{ fontSize: 40, fontWeight: 800, color: "var(--oo-surface-2)" }}>{item.no}</span>
                    </div>
                    <h3 className="font-display" style={{ fontSize: 22, fontWeight: 700, marginTop: 24 }}>{item.title}</h3>
                    <p style={{ color: "var(--oo-muted)", lineHeight: 1.7, marginTop: 14, fontSize: 15 }}>{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED WORK */}
      <section className="oo-section">
        <div className="oo-container">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(32px,5vw,56px)", letterSpacing: "-0.02em" }}>
                Featured <span className="text-accent">Work</span>
              </h2>
            </Reveal>
            <Reveal delay={80}>
              <Link to="/work" className="hover-underline font-display inline-flex items-center gap-2" style={{ color: "var(--oo-muted)" }}>
                View All Projects <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>

          <div className="grid gap-6 md:grid-cols-3" style={{ marginTop: 52 }}>
            {featured.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <Link to="/work" className="oo-card block h-full overflow-hidden group" style={{ padding: 0 }}>
                  <div style={{ overflow: "hidden", aspectRatio: "4/3" }}>
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" style={{ transition: "transform 0.6s ease" }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.06)")}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    />
                  </div>
                  <div style={{ padding: 26 }}>
                    <span className="oo-eyebrow">{p.tag}</span>
                    <h3 className="font-display" style={{ fontSize: 20, fontWeight: 700, marginTop: 12 }}>{p.title}</h3>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="oo-section">
        <div className="oo-container">
          <Reveal>
            <div className="relative overflow-hidden" style={{ borderRadius: 28, border: "1px solid var(--oo-border)", background: "linear-gradient(135deg, var(--oo-surface) 0%, var(--oo-bg-2) 100%)", padding: "clamp(40px,6vw,80px)" }}>
              <div className="glow-orb" style={{ width: 360, height: 360, background: "var(--oo-orange)", bottom: "-40%", right: "-6%", opacity: 0.22 }} />
              <div className="absolute inset-0 grid-overlay" style={{ opacity: 0.5 }} />
              <div className="relative" style={{ maxWidth: 720 }}>
                <h2 className="font-display font-extrabold" style={{ fontSize: "clamp(32px,5vw,56px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}>
                  Let's Create <span className="text-accent">Something Bold</span>
                </h2>
                <p style={{ color: "var(--oo-muted)", fontSize: 18, lineHeight: 1.7, marginTop: 22 }}>
                  Whether you're a brand, government body, or media owner — we bring the infrastructure, creativity, and distribution to make your message unavoidable.
                </p>
                <Link to="/contact" className="btn-primary" style={{ marginTop: 34 }}>
                  Start a Conversation <ArrowUpRight size={18} />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
